/**
 * Admin Bar Controller
 * Alpine.js component for admin bar status display
 */

import { checkAppStatus } from '../services/appStatusService.js';
import { SocketController } from './socketController.js';

/**
 * Create Alpine.js admin bar component
 * @returns {object} Alpine.js component
 */
export function createAdminBarController() {
	return {
		isChecking: true,
		isHealthy: false,
		isDeviceConnected: false,
		devicePid: '',
		devicePid: '',
		serverAddress: '',
		serverRunning: false,
		isInitialized: false,
		socketConnected: false,
		socketConnecting: false,
		socketConnectable: false,

		// Socket controller
		socketController: null,

		/**
		 * Debug logging helper
		 */
		debugLog(...args) {
			if (this.config && this.config.debug) {
				console.log(...args);
			}
		},

		async init() {
			// Initialize socket controller
			this.initializeSocketController();
			
			await this.checkConnection();
			this.isInitialized = true;
			this.updateConditionalMenus();
		},

		async checkConnection() {
			this.isChecking = true;

			try {
				const config = window.rightplaceConfig || {};
				const appStatus = await checkAppStatus(config);

				// Update component state based on app status
				this.isHealthy = appStatus.success;
				this.serverRunning = appStatus.externalServerRunning;
				this.devicePid = appStatus.devicePid || '';
				this.isDeviceConnected = appStatus.isDeviceConnected;
				this.serverAddress = appStatus.externalServerAddress;
				
				// Check socket connection status
				if (this.socketController) {
					const socketStatus = this.socketController.getStatus();
					this.socketConnected = socketStatus.isConnected;
					this.socketConnecting = socketStatus.isConnecting;
				}
				
				// Determine if socket is connectable (server is available)
				this.socketConnectable = appStatus.externalServerRunning && appStatus.success;
			} catch (error) {
				if (this.config && this.config.debug) {
					console.error('[rpAdminBar] Error checking connection:', error);
				}
				this.isHealthy = false;
				this.isDeviceConnected = false;
				this.serverRunning = false;
			} finally {
				this.isChecking = false;
				this.updateConditionalMenus();
			}
		},

		/**
		 * Initialize socket controller
		 */
		initializeSocketController() {
			const config = window.rightplaceConfig || {};
			// Get singleton instance
			this.socketController = SocketController.getInstance(config);
			
			// Create unique callback references to prevent duplicates
			this.socketCallbacks = {
				connected: (data) => {
					this.debugLog('[AdminBarController] Socket connected:', data);
					// Update socket connection status
					this.socketConnected = true;
					this.socketConnecting = false;
					// Update status when socket connects
					this.checkConnection();
				},
				disconnected: (data) => {
					this.debugLog('[AdminBarController] Socket disconnected:', data);
					// Update socket connection status
					this.socketConnected = false;
					this.socketConnecting = false;
					// Update status when socket disconnects
					this.isHealthy = false;
					this.isDeviceConnected = false;
					this.serverRunning = false;
					this.updateConditionalMenus();
				},
				statusUpdate: (data) => {
					this.debugLog('[AdminBarController] Socket status update:', data);
					// Handle status updates from socket
					this.handleSocketStatusUpdate(data);
				}
			};
			
			// Set up socket event listeners with component name
			this.socketController.on('AdminBarController', 'connected', this.socketCallbacks.connected);
			this.socketController.on('AdminBarController', 'disconnected', this.socketCallbacks.disconnected);
			this.socketController.on('AdminBarController', 'statusUpdate', this.socketCallbacks.statusUpdate);
			
			// Initialize socket connection (handles duplicate prevention internally)
			this.socketController.init();
		},

		/**
		 * Handle socket status updates
		 */
		handleSocketStatusUpdate(data) {
			// Update status based on socket updates
			if (data.type === 'status' && data.data) {
				this.isHealthy = data.data.success || false;
				this.isDeviceConnected = data.data.isDeviceConnected || false;
				this.serverRunning = data.data.externalServerRunning || false;
				this.devicePid = data.data.devicePid || '';
				this.serverAddress = data.data.externalServerAddress || '';
				this.updateConditionalMenus();
			}
		},

		updateConditionalMenus() {
			// Show/hide conditional menus based on connection status
			const shouldShow = this.isHealthy && !this.isChecking;
			const conditionalMenus = document.querySelectorAll('#wpadminbar .rightplace-conditional-menu');
			
			conditionalMenus.forEach(menu => {
				if (shouldShow) {
					menu.classList.add('show');
				} else {
					menu.classList.remove('show');
				}
			});
		}
	};
}

